import React, { useEffect, useState, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Loader } from "@aws-amplify/ui-react";
import ActionIcon from "components/actions/ActionIcon";
import {
  util,
  Canvas,
  Image as FabricImage,
  Polyline,
  Polygon,
  Group,
  Text,
  Circle,
  Rect,
} from "fabric";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import {
  createCropUnit,
  updateCropUnit,
} from "graphql/mutations";
import MicrolotNamePopup from "components/agro/lots/MicrolotNamePopup";

const placeholderStyle = {
  width: "100%",
  height: 360,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px dashed #ccc",
  background: "#fafafa",
  color: "#666",
};

const overlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.7)",
  zIndex: 2,
};

const getPolygonCentroid = (pts) => {
  let first = pts[0],
    last = pts[pts.length - 1];
  if (first.x !== last.x || first.y !== last.y) pts.push(first);
  let twicearea = 0,
    x = 0,
    y = 0,
    nPts = pts.length,
    p1,
    p2,
    f;
  for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
    p1 = pts[i];
    p2 = pts[j];
    f = p1.x * p2.y - p2.x * p1.y;
    twicearea += f;
    x += (p1.x + p2.x) * f;
    y += (p1.y + p2.y) * f;
  }
  f = twicearea * 3;
  return { x: x / f, y: y / f };
};

const LotDemarcationCanvas = ({
  imageUrl,
  cropUnits = [],
  readOnly = false,
  lotId,
  companyID,
  farmID,
  onCropUnitChange,
  isLoadingData = false,
}) => {
  // --- State ---
  const [mode, setMode] = useState("select");
  const [loadingAction, setLoadingAction] = useState(false);

  // Popup State
  const [popupState, setPopupState] = useState({
    isOpen: false,
    position: null,
    initialValue: "",
    isNew: false,
    target: null,
  });
  const [isLoadingPopup, setIsLoadingPopup] = useState(false);
  const popupStateRef = useRef(popupState);

  // Refs
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const cropUnitsRef = useRef(cropUnits);
  const drawingStateRef = useRef({
    points: [],
    tempPoly: null,
    vertices: [],
    mode: "select",
    closeMarker: null,
  });

  // Thresholds
  const CLOSE_THRESHOLD = 12;

  useEffect(() => {
    cropUnitsRef.current = cropUnits;
  }, [cropUnits]);

  useEffect(() => {
    popupStateRef.current = popupState;
  }, [popupState]);

  // Sync mode state with ref for event handlers
  useEffect(() => {
    drawingStateRef.current.mode = mode;
  }, [mode]);

  // --- Drawing Logic ---

  const drawCropUnits = async (canvas, units) => {
    if (!canvas || !units) return;

    // Removing existing microlotes
    const objs = canvas.getObjects();
    objs.forEach(o => {
      if (o.__microlote) canvas.remove(o);
    });

    const drawingDataWithIds = units
      .map((unit) => {
        if (!unit.drawingData) return null;
        try {
          return {
            id: unit.id,
            data: JSON.parse(unit.drawingData),
          };
        } catch (e) {
          console.error("Error parsing drawing data", unit.id, e);
          return null;
        }
      })
      .filter(Boolean);

    for (let i = 0; i < drawingDataWithIds.length; i++) {
      const item = drawingDataWithIds[i];
      try {
        const enlivenedObjects = await util.enlivenObjects([item.data]);
        const obj = enlivenedObjects[0];
        if (obj) {
          obj.set({
            selectable: !readOnly,
            evented: !readOnly,
            objectCaching: false,
            __microlote: true,
            cropUnitId: item.id,
            hoverCursor: readOnly ? 'default' : 'move'
          });

          // If readOnly, ensure sub-objects are also not interactive if any
          if (readOnly && obj.type === 'group') {
            obj.forEachObject(o => {
              o.set({ evented: false, selectable: false });
            });
          }

          canvas.add(obj);
        }
      } catch (e) {
        console.error("Error enlivening obj", e);
      }
    }
    canvas.renderAll();
  };

  // --- Handlers ---

  const cleanupDrawingState = () => {
    const canvas = fabricRef.current;
    const state = drawingStateRef.current;
    if (!canvas || !state) return;

    if (state.tempPoly) {
      try { canvas.remove(state.tempPoly); } catch (e) { }
    }
    if (state.vertices) {
      state.vertices.forEach((v) => {
        try { canvas.remove(v); } catch (e) { }
      });
    }
    if (state.closeMarker) {
      try { canvas.remove(state.closeMarker); } catch (e) { }
    }

    state.points = [];
    state.tempPoly = null;
    state.vertices = [];
    state.closeMarker = null;

    setMode("select");
    state.mode = "select";
    try {
      if (canvas && canvas.upperCanvasEl)
        canvas.upperCanvasEl.style.cursor = "default";
    } catch (e) { }
    canvas.renderAll();
  };

  const handleSaveName = async (name) => {
    const canvas = fabricRef.current;
    if (!canvas || !name) {
      handleClosePopup();
      return;
    }
    setIsLoadingPopup(true);

    try {
      if (popupState.isNew && popupState.target) {
        // CREATE
        const points = popupState.target;
        // ... Polygon creation logic similar to previous ...
        const polygon = new Polygon(points, {
          stroke: "#0d6efd",
          strokeWidth: 3,
          fill: "rgba(13, 110, 253, 0.2)",
          objectCaching: false,
          originX: "center",
          originY: "center"
        });
        const center = getPolygonCentroid(points);
        const textObj = new Text(name, {
          fontSize: 12,
          fill: "#ffffff",
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          originX: "center",
          originY: "center"
        });
        const paddingX = 10, paddingY = 4;
        const bgRect = new Rect({
          width: textObj.width + paddingX * 2,
          height: textObj.height + paddingY * 2,
          fill: "#0d6efd",
          rx: 16, ry: 16,
          originX: "center",
          originY: "center"
        });

        polygon.set({ left: center.x, top: center.y });
        // Clean polygon for grouped
        const cleanPolygon = new Polygon(points, {
          stroke: "#0d6efd",
          strokeWidth: 3,
          fill: "rgba(13, 110, 253, 0.2)",
          originX: "center",
          originY: "center"
        });

        bgRect.set({ left: center.x, top: center.y });
        textObj.set({ left: center.x, top: center.y });

        const group = new Group([cleanPolygon, bgRect, textObj], {
          selectable: true,
          objectCaching: false,
          __microlote: true,
          subTargetCheck: true,
          interactive: false
        });

        if (!companyID || !farmID || !lotId) {
          toast.error(strings.responses.errorLotInfo);
          return;
        }

        const drawingData = JSON.stringify(group.toObject(["__microlote", "cropUnitId"]));
        const input = {
          name,
          lotID: lotId,
          companyID: companyID,
          farmID: farmID,
          drawingData
        };

        const res = await API.graphql(graphqlOperation(createCropUnit, { input }));
        const newUnit = res.data.createCropUnit;
        // Update local canvas object with ID
        group.cropUnitId = newUnit.id;
        canvas.add(group);
        toast.success(strings.responses.successCreate);

        if (onCropUnitChange) onCropUnitChange(); // Notify parent
        handleClosePopup();

      } else if (!popupState.isNew && popupState.target) {
        // UPDATE NAME
        const group = popupState.target;
        // ... update visual text logic ...
        const objects = group.getObjects();
        const textObject = objects.find(o => o.type === "text");
        const bgRect = objects.find(o => o.type === "rect" && o !== group);

        if (textObject && textObject.text !== name) {
          const oldName = textObject.text;
          textObject.set("text", name);
          if (bgRect) {
            bgRect.set({
              width: textObject.width + 20,
              height: textObject.height + 8
            });
            group.remove(bgRect); group.remove(textObject);
            group.add(bgRect); group.add(textObject);
          }

          try {
            const drawingData = JSON.stringify(group.toObject(["__microlote", "cropUnitId"]));
            const input = {
              id: group.cropUnitId,
              name,
              drawingData
            }
            await API.graphql(graphqlOperation(updateCropUnit, { input }));
            toast.success(strings.responses.successUpdateName);
            if (onCropUnitChange) onCropUnitChange();
            handleClosePopup();
          } catch (err) {
            console.error(err);
            textObject.set("text", oldName);
            canvas.renderAll();
            toast.error(strings.responses.errorUpdateName);
          }
          canvas.renderAll();
        } else {
          handleClosePopup();
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Error saving");
      handleClosePopup();
    } finally {
      setIsLoadingPopup(false);
    }
  };

  const handleClosePopup = () => {
    cleanupDrawingState();
    setPopupState({ isOpen: false, position: null, target: null });
  };

  const finalizeDrawing = () => {
    const canvas = fabricRef.current;
    const state = drawingStateRef.current;
    if (!canvas || !state.points || state.points.length < 3) return;

    try {
      const center = getPolygonCentroid(state.points);
      const canvasEl = canvas.getElement();
      const rect = canvasEl.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      const popupPos = {
        top: rect.top + scrollTop + center.y,
        left: rect.left + scrollLeft + center.x
      };

      setPopupState({
        isOpen: true,
        position: popupPos,
        isNew: true,
        target: [...state.points],
        initialValue: strings.microlot.defaultName
      });
    } catch (e) {
      console.error(e);
      cleanupDrawingState();
    }
  };

  const cancelDrawing = () => {
    cleanupDrawingState();
  };

  // --- Fabric Initialization & Effects ---

  useEffect(() => {
    if (!canvasRef.current || !imageUrl) return;

    if (fabricRef.current) {
      try { fabricRef.current.dispose(); } catch (e) { }
      fabricRef.current = null;
    }

    const canvasEl = canvasRef.current;
    const canvas = new Canvas(canvasEl, {
      selection: false,
      hoverCursor: readOnly ? 'default' : 'move'
    });
    fabricRef.current = canvas;

    // Sizing logic ... (simplified for brevity but robust)
    const parentWidth = canvasEl.parentElement ? canvasEl.parentElement.clientWidth : 900;
    const width = Math.min(900, parentWidth - 20 || 900);
    const height = 600;
    canvas.setDimensions({ width, height });

    // Force style match
    if (canvas.lowerCanvasEl) {
      canvas.lowerCanvasEl.style.width = width + 'px';
      canvas.lowerCanvasEl.style.height = height + 'px';
    }
    if (canvas.upperCanvasEl) {
      canvas.upperCanvasEl.style.width = width + 'px';
      canvas.upperCanvasEl.style.height = height + 'px';
    }

    // Load Image
    const applyImage = (img) => {
      if (!canvas) return;
      img.set({
        left: 0, top: 0,
        originX: "left", originY: "top",
        selectable: false, evented: false
      });
      const iw = img._element?.naturalWidth || img.width || img.getScaledWidth();
      const ih = img._element?.naturalHeight || img.height || img.getScaledHeight();

      canvas.setDimensions({ width: iw, height: ih });
      if (canvas.lowerCanvasEl) {
        canvas.lowerCanvasEl.style.width = iw + 'px';
        canvas.lowerCanvasEl.style.height = ih + 'px';
      }
      if (canvas.upperCanvasEl) {
        canvas.upperCanvasEl.style.width = iw + 'px';
        canvas.upperCanvasEl.style.height = ih + 'px';
      }

      // Add
      img.set({ scaleX: 1, scaleY: 1 });
      img.__isBackground = true;

      // Remove old background if exists
      const oldBg = canvas.getObjects().find(o => o.__isBackground);
      if (oldBg) canvas.remove(oldBg);

      canvas.add(img);
      canvas.moveObjectTo(img, 0);

      // Now draw units using ref to avoid stale closure
      drawCropUnits(canvas, cropUnitsRef.current);
    };

    const loadImgElement = (src) => new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = src;
    });

    loadImgElement(imageUrl)
      .then(imgEl => {
        const fImg = new FabricImage(imgEl);
        applyImage(fImg);
      })
      .catch(() => {
        // fallback blob
        fetch(imageUrl).then(r => r.blob()).then(blob => {
          const url = URL.createObjectURL(blob);
          loadImgElement(url).then(imgEl2 => {
            const fImg2 = new FabricImage(imgEl2);
            applyImage(fImg2);
            URL.revokeObjectURL(url);
          });
        });
      });

    // Events
    if (!readOnly) {
      canvas.on("mouse:down", (opt) => {
        const state = drawingStateRef.current;
        if (state.mode !== 'draw') return;
        if (opt.target) return; // Clicking object

        let pointer = canvas.getScenePoint(opt.e);

        // Logic for finalize
        if (state.points.length >= 3) {
          const first = state.points[0];
          const dx = pointer.x - first.x;
          const dy = pointer.y - first.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= CLOSE_THRESHOLD) {
            finalizeDrawing();
            return;
          }
        }

        state.points.push({ x: pointer.x, y: pointer.y });

        // Visuals
        const r = 5;
        const c = new Circle({
          left: pointer.x, top: pointer.y, radius: r,
          fill: "#fff", stroke: "#1976d2", strokeWidth: 1.5,
          originX: "center", originY: "center",
          selectable: false, evented: false
        });
        canvas.add(c);
        state.vertices.push(c);

        if (state.tempPoly) {
          try { canvas.remove(state.tempPoly); } catch (e) { }
        }
        const p = new Polyline(state.points, {
          stroke: "red", strokeWidth: 2, fill: "rgba(255,0,0,0.2)",
          selectable: false, evented: false
        });
        state.tempPoly = p;
        canvas.add(p);
        canvas.renderAll();
      });

      canvas.on("mouse:move", (opt) => {
        const state = drawingStateRef.current;
        if (state.mode !== 'draw') return;
        let pointer = canvas.getScenePoint(opt.e);

        if (state.tempPoly) {
          const pts = state.points.concat([{ x: pointer.x, y: pointer.y }]);
          state.tempPoly.set({ points: pts });
          canvas.renderAll();
        }
        // Helper for close marker (simplified)
        if (state.points.length >= 3) {
          const first = state.points[0];
          const dx = pointer.x - first.x;
          const dy = pointer.y - first.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= CLOSE_THRESHOLD) {
            // show marker
            if (!state.closeMarker) {
              const m = new Circle({
                left: first.x, top: first.y, radius: 12,
                fill: "rgba(0,0,0,0)", stroke: "lime", strokeWidth: 2,
                originX: "center", originY: "center", selectable: false, evented: false
              });
              state.closeMarker = m;
              canvas.add(m);
              canvas.bringObjectToFront(m);
            }
          } else {
            if (state.closeMarker) {
              canvas.remove(state.closeMarker);
              state.closeMarker = null;
            }
          }
          canvas.renderAll();
        }
      });

      canvas.on("mouse:dblclick", (opt) => {
        if (drawingStateRef.current.mode === 'draw' || popupStateRef.current.isOpen) return;

        if (opt.target && opt.target.__microlote && opt.target.type === 'group') {
          // Edit name
          const group = opt.target;
          const textObj = group.getObjects().find(o => o.type === 'text');
          if (!textObj) return;

          const canvasEl = canvas.getElement();
          const rect = canvasEl.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          const groupCenter = group.getCenterPoint();

          const popupPos = {
            top: rect.top + scrollTop + groupCenter.y,
            left: rect.left + scrollLeft + groupCenter.x,
          };

          setPopupState({
            isOpen: true,
            position: popupPos,
            isNew: false,
            target: group,
            initialValue: textObj.text
          });
        }
      });

      canvas.on("object:modified", async (opt) => {
        if (opt.target && opt.target.__microlote && opt.target.cropUnitId) {
          // Geometry changed
          const group = opt.target;
          setLoadingAction(true);
          try {
            const drawingData = JSON.stringify(group.toJSON(["__microlote"]));
            const input = {
              id: group.cropUnitId,
              drawingData
            };
            await API.graphql(graphqlOperation(updateCropUnit, { input }));
            toast.success(strings.responses.successUpdate);
            if (onCropUnitChange) onCropUnitChange();
          } catch (e) {
            console.error(e);
            toast.error(strings.responses.errorUpdate);
            // Revert? (Complex)
          } finally {
            setLoadingAction(false);
          }
        }
      });
    }

    const handleKeyDown = async (e) => {
      if (readOnly) return;
      if (e.key === "Delete" || e.key === "Del" || e.keyCode === 46) {
        const active = canvas.getActiveObject();
        if (active && active.__microlote && active.cropUnitId) {
          setLoadingAction(true);
          try {
            await API.graphql(graphqlOperation(updateCropUnit, {
              input: {
                id: active.cropUnitId,
                deletedAt: new Date().toISOString()
              }
            }));
            canvas.remove(active);
            toast.success(strings.responses.successDelete);
            if (onCropUnitChange) onCropUnitChange();
          } catch (err) {
            console.error(err);
            toast.error(strings.responses.errorDelete);
          } finally {
            setLoadingAction(false);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => { // Cleanup
      window.removeEventListener("keydown", handleKeyDown);
      try { canvas.dispose(); } catch (e) { }
      fabricRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, readOnly]); // Re-init if image changes or mode changes

  useEffect(() => {
    if (fabricRef.current) {
      drawCropUnits(fabricRef.current, cropUnits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropUnits]);


  // --- Render ---

  if (!imageUrl) {
    return (
      <div style={placeholderStyle}>
        {isLoadingData ? (
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Loader /> {strings.loading}
          </span>
        ) : (
          strings.placeholder
        )}
      </div>
    );
  }

  return (
    <>
      <MicrolotNamePopup
        isOpen={popupState.isOpen}
        position={popupState.position}
        initialValue={popupState.initialValue}
        onSave={handleSaveName}
        onClose={handleClosePopup}
        isLoading={isLoadingPopup}
        title={
          popupState.isNew
            ? strings.microlot.createTitle
            : strings.microlot.editTitle
        }
      />

      <div style={{ position: "relative" }}>
        <div style={{ position: "relative", textAlign: "center" }}>
          {/* Overlays */}
          {(isLoadingData || loadingAction) && (
            <div style={overlayStyle}>
              <Loader />
            </div>
          )}

          {/* Toolbar (Only if not ReadOnly) */}
          {!readOnly && (
            <div style={{ marginBottom: 8, display: "flex", gap: 8, justifyContent: "center" }}>
              <ActionIcon
                name={strings.drawing.draw}
                icon="Edit"
                showName={true}
                onClick={() => {
                  setMode("draw");
                  // Deselect active
                  if (fabricRef.current) {
                    fabricRef.current.discardActiveObject();
                    fabricRef.current.renderAll();
                    try {
                      if (fabricRef.current.upperCanvasEl) fabricRef.current.upperCanvasEl.style.cursor = 'crosshair';
                    } catch (e) { }
                  }
                }}
              />
              {mode === 'draw' && (
                <ActionIcon
                  name={strings.drawing.cancel}
                  icon="Close"
                  showName={true}
                  onClick={cancelDrawing}
                />
              )}
            </div>
          )}

          <div
            style={{
              border: "1px solid #ddd",
              display: "inline-block",
              borderRadius: 6,
              overflow: "visible",
            }}
          >
            <canvas ref={canvasRef} />
          </div>
        </div>
      </div>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    loading: "Cargando...",
    placeholder: "No hay foto o datos para mostrar.",
    microlot: {
      createTitle: "Crear microlote",
      editTitle: "Editar microlote",
      defaultName: "Microlote",
    },
    drawing: {
      draw: "Dibujar línea",
      cancel: "Cancelar línea",
    },
    responses: {
      errorLotInfo: "Falta información del lote.",
      errorUpload: "Error al subir la foto",
      successCreate: "Microlote creado.",
      errorCreate: "Error al crear el microlote.",
      successUpdateName: "Nombre del microlote actualizado.",
      errorUpdateName: "Error al actualizar el nombre.",
      successUpdate: "Microlote actualizado.",
      errorUpdate: "Error al actualizar el microlote.",
      successDelete: "Microlote eliminado.",
      errorDelete: "Error al eliminar el microlote.",
    }
  },
});

export default LotDemarcationCanvas;
